import { startOfToday, addDays, format as jFormat } from "date-fns-jalali";
import data from "@/public/data/hotel/confirm.json";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { type AnySchema, object, type ObjectSchema, string } from "yup";
import { getYear, subYears } from "date-fns-jalali";
import type {
  ExistedRoom,
  MinPriceRoom,
} from "@/types/services/hotel/apis/Detail";
/**
 *
 * @param count the count of days generation
 * @param initialDate the start date to generate date from
 * @returns a list of dates started by initialDate with the count of count parameter
 */

export function generateStayDays(count = 20, initialDate?: Date) {
  const today = initialDate ?? startOfToday();
  const days = [];

  for (let x = 1; x <= count; ++x) {
    days.push(addDays(today, x));
  }

  return days;
}

export function countRooms(rooms: { adult: number; child: number[] }[]) {
  return rooms.reduce((curr, acc) => curr + (acc.adult + acc.child.length), 0);
}

export function createRoomsPattern(
  type: "encode",
  value: { adult: number; child: (string | undefined)[] }[],
): string;
export function createRoomsPattern(
  type: "decode",
  value: string,
): { adult: number; child: string[] }[];

// Implementation
export function createRoomsPattern(
  type: "encode" | "decode",
  value: { adult: number; child: (string | undefined)[] }[] | string,
) {
  if (type === "encode") {
    const rooms = value as { adult: number; child: (string | undefined)[] }[];
    let slug = "";
    for (let z = 0; z < rooms.length; ++z) {
      const { adult, child } = rooms[z];
      slug += adult;
      if (child.length !== 0) slug += "-";
      for (let x = 0; x < child.length; ++x) {
        if (child[x] !== undefined) {
          // Skip undefined values
          slug += child[x];
          if (child[x + 1] !== undefined) slug += "-"; // Only add "-" if next value exists
        }
      }
      if (rooms[z + 1]) slug += "_";
    }
    return slug;
  }
  const rooms = (value as string).split("_");
  const roomArr = [];

  for (const room of rooms) {
    roomArr.push({
      adult: +room.split("-")[0],
      child: room
        .split("-")
        .slice(1)
        .map((child) => child),
    });
  }

  return roomArr;
}

export function isEmpty(
  value: string | number | Record<string, any> | any[],
): boolean {
  if (typeof value === "string") {
    return value.trim() === "";
  }
  if (typeof value === "number") {
    return value === 0;
  }
  if (Array.isArray(value) || typeof value === "object") {
    return Object.values(value).every((val) => isEmpty(val));
  }
  return false;
}

const { hour, day } = data;

export function formatDateForConfirmedPage(date: Date) {
  const turnedToDate = new Date(date);
  const parsedFormat = jFormat(turnedToDate, `EEEE d MMMM ${hour} H:m`);
  return parsedFormat;
}

export function formatDaysForConfirmPage(input: number) {
  return `${input} ${day}`;
}

export function formatGender(input: "male" | "female") {
  return input === "male" ? "آقا" : "خانم";
}

export function createDetailRoomList(existedRooms: ExistedRoom[]) {
  const rooms: MinPriceRoom[] = [];

  for (const room of existedRooms) {
    for (const ratePlan of room.rate_plans) {
      rooms.push({
        ...room,
        rate_plans: ratePlan,
      });
    }
  }
  return rooms.sort((a, b) => a.rate_plans.price - b.rate_plans.price);
}

export const createQueryString = (
  searchParams: ReadonlyURLSearchParams | { [key: string]: string },
  newParams: { name: string; value?: string; action?: "add" | "remove" }[],
  action: "add" | "remove",
) => {
  const params = new URLSearchParams(searchParams.toString());

  if (action === "add") {
    for (const x of newParams) {
      const { name, value, action } = x;
      if (action && action === "remove") {
        params.delete(name);
      } else if (value) {
        params.set(name, value);
      }
    }
  } else {
    for (const x of newParams) {
      const { name } = x;
      params.delete(name);
    }
  }

  return params.toString();
};

export function isValidIranianNationalCode(input: string): boolean {
  if (!/^\d{10}$/.test(input)) return false;

  if (/^(\d)\1+$/.test(input)) return false;

  const check = +input[9];
  const sum =
    Array.from({ length: 9 }, (_, i) => +input[i] * (10 - i)).reduce(
      (x, y) => x + y,
    ) % 11;
  return (sum < 2 && check === sum) || (sum >= 2 && check + sum === 11);
}

export function generateRoomIds(rooms: MinPriceRoom[]) {
  let roomIds = "";

  for (let x = 0; x < rooms.length; ++x) {
    roomIds += `${rooms[x].id}_${rooms[x].rate_plans.id}_${rooms[x].count}`;

    if (x !== rooms.length - 1) roomIds += "-";
  }

  return roomIds;
}

export function extractRoomsFromDataWithRoomIds(rooms: string) {
  const generatedRoomsWithRoomIds: { roomId: number; ratePlanId: number }[] =
    [];

  const roomsList = rooms.split("-");

  for (const room of roomsList) {
    const roomDetails = room.split("_");
    const roomId = +roomDetails[0];
    const roomRatePlanId = +roomDetails[1];
    const roomsCount = +roomDetails[2];

    for (let count = 1; count <= roomsCount; ++count) {
      generatedRoomsWithRoomIds.push({
        roomId: roomId,
        ratePlanId: roomRatePlanId,
      });
    }
  }

  return generatedRoomsWithRoomIds;
}

export function transformExistedRoomsToMinPriceRooms(
  existedRooms: ExistedRoom[],
  minPriceRoomsData: { roomId: number; ratePlanId: number }[],
): MinPriceRoom[] {
  const result: MinPriceRoom[] = [];

  for (const roomData of minPriceRoomsData) {
    const room = existedRooms?.find((room) => room.id === roomData.roomId);
    const ratePlan = room?.rate_plans.find(
      (rateplan) => rateplan.id === roomData.ratePlanId,
    );

    if (room && ratePlan) {
      const { id, type, name, capacity, extra_capacity } = room;

      const minPriceRoom: MinPriceRoom = {
        id,
        type,
        name,
        capacity,
        extra_capacity,
        // TODO: Change this later
        number: 1,
        rate_plans: ratePlan,
        count: 1,
      };

      result.push(minPriceRoom);
    }
  }

  return result;
}

export function createRoomsFields(rooms: MinPriceRoom[]) {
  return rooms.map(({ id, name, capacity, rate_plans }) => ({
    id,
    rate_plan_id: rate_plans.id,
    capacity,
    name,
    ratePlanName: rate_plans.name,
    passengers: new Array(capacity).fill({
      firstName: "",
      lastName: "",
      nationalCode: "",
      gender: "male",
      type: "adl",
    }),
  }));
}

export function withDefaults<Datatype>(data: Datatype, defaults: Datatype) {
  return data ? { ...defaults, ...data } : defaults;
}

export const getCookie = (name: string): string | null => {
  if (typeof document !== "undefined") {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0)
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
};

/**
 * Set a cookie
 * @param name - The name of the cookie
 * @param value - The value of the cookie
 * @param days - Optional number of days until the cookie expires
 * @param path - Optional cookie path (default is '/')
 * @param domain - Optional cookie domain
 * @param secure - Optional secure flag
 */
export const setCookie = (
  name: string,
  value: string,
  days?: number,
  path = "/",
  domain?: string,
  secure?: boolean,
): void => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);

    expires = `; expires=${date.toUTCString()}`;
  }
  const cookieValue = encodeURIComponent(value);
  let cookie = `${name}=${cookieValue}${expires}; path=${path}`;
  if (domain) {
    cookie += `; domain=${domain}`;
  }
  if (secure) {
    cookie += "; secure";
  }
  if (document) document.cookie = cookie;
};

/**
 *
 * @param input - Number input
 * @returns
 */

export function convertToEnglishDigits(input: string): string {
  const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  const arabicDigits = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

  return input.replace(/[۰-۹٠-٩]/g, (char) => {
    if (persianDigits.includes(char)) {
      return persianDigits.indexOf(char).toString();
    }
    if (arabicDigits.includes(char)) {
      return arabicDigits.indexOf(char).toString();
    }
    return char;
  });
}

export function buildDynamicSchema(
  fields: PaymentRequirement[],
): ObjectSchema<Record<string, AnySchema>> {
  const shape: Record<string, AnySchema> = {};

  fields.forEach(({ column, type, label }) => {
    let schema: AnySchema;

    switch (type) {
      case "number":
        schema = string()
          .required(`فیلد ${label} الزامی می باشد`)
          .typeError(`فیلد ${label} باید عدد باشد`);

        break;
      case "email":
        schema = string().email(`${label} must be a valid email`).optional();
        break;
      case "text":
        schema = string().optional();
        break;
      default:
        schema = string().optional();
        break;
    }

    shape[column] = schema;
  });

  return object().shape(shape);
}

export const convertAgeToYear = (age: string | undefined): number | null => {
  if (!age || Number.isNaN(Number(age))) return null;
  return getYear(subYears(new Date(), Number(age)));
};
